<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="8">
                    <h1>Product</h1>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                    <v-text-field
                        v-model="data.quickSearchVal"
                        :label="CommonLabelTextHelper.getLabelText.filterCurrentPage"
                        @keydown="onKeydownQuickSearch"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-expansion-panels v-model="data.panel" multiple>
                <v-expansion-panel>
                    <v-expansion-panel-title>
                        <h2>Advanced Search</h2>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                        <date-range-select
                            v-model="data.searchFilterDateRangeGrp.dateCreateAt"
                            :from-label="formLabelText.dateCreateAtFrom"
                            :to-label="formLabelText.dateCreateAtTo"
                            :enable-validation="true"
                            @validation-change="onDateRangeValidationChange"
                        />
                        <v-row>
                            <v-col cols="12" sm="6" md="6">
                                <v-text-field
                                    v-model="data.searchFilters.productId"
                                    label="Product ID"
                                    clearable
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6" md="6">
                                <v-text-field v-model="data.searchFilters.sku" label="SKU" clearable></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="6" md="6">
                                <v-select
                                    v-model="data.searchFilters.categoryCode"
                                    :items="categoryOptions"
                                    item-title="code"
                                    item-value="code"
                                    :label="formLabelText.categoryCode"
                                    clearable
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6" md="6">
                                <v-text-field v-model="data.searchFilters.title" label="Title" clearable></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" sm="12" md="12">
                                <v-text-field
                                    v-model="data.searchFilters.description"
                                    label="Description"
                                    clearable
                                ></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row class="mt-2">
                            <v-btn class="mr-2" variant="text" @click="onResetClick">
                                <v-icon>mdi-cached</v-icon>
                                Clear
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn class="mr-2" variant="text" @click="onSearchClick">
                                <v-icon>mdi-magnify</v-icon>
                                Search
                            </v-btn>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="data.deleteOperation.showConfirmDialog" max-width="500">
                <v-card>
                    <v-card-title class="headline">
                        <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                        Confirm Delete
                    </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this product?
                        <br /><br />
                        <strong>Product ID:</strong> {{ data.deleteOperation.selectedProduct?.productId || '' }}
                        <br />
                        <strong>SKU:</strong> {{ data.deleteOperation.selectedProduct?.sku || '' }} <br /><br />
                        <v-alert type="warning" variant="tonal"> This action cannot be undone. </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="onCancelDelete" :disabled="data.deleteOperation.isLoading"> Cancel </v-btn>
                        <v-btn
                            color="error"
                            @click="onConfirmDelete"
                            :loading="data.deleteOperation.isLoading"
                            :disabled="data.deleteOperation.isLoading"
                        >
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-card>
                <v-card-title>
                    <div class="d-flex flex-wrap align-center ga-2 w-100">
                        <v-btn color="success" @click="onImportClick"> Import Product </v-btn>
                        <v-btn variant="text" @click="onSearchClick">
                            <v-icon>mdi-cached</v-icon>
                            Refresh
                        </v-btn>
                        <v-spacer></v-spacer>
                        <v-btn color="warning" @click="onImportProductCostClick"> Import Cost </v-btn>
                        <v-btn color="primary" @click="onImportEstimatedShippingFeeClick">
                            Import Estimated Shipping
                        </v-btn>
                        <export-to-csv
                            :data="currentPageData"
                            :csv-header="data.csvExportConfig.csvHeader"
                            :filename="`exportProduct_page${pagination.currentPage}.csv`"
                            :formatter="csvFormatter"
                        ></export-to-csv>
                    </div>
                </v-card-title>
                <v-card-text>
                    <!-- Generic Table Component -->
                    <GenericListTable
                        :pagination="pagination"
                        :current-page-data="currentPageData"
                        :headers="headers"
                        :primary-key="'productId'"
                        :action-header-value="actionHeaderItem.value"
                        :quick-search-value="data.quickSearchVal"
                        :loading-message="'Loading more products...'"
                        :is-loading="data.deleteOperation.isLoading"
                        @page-size-change="onPageSizeChange"
                        @page-select="onPageSelect"
                        @next-page="onNextPage"
                        @previous-page="onPreviousPage"
                    >
                        <template #table-actions="{ item, isLoading }">
                            <v-btn
                                :disabled="isLoading"
                                variant="text"
                                icon="mdi-pencil"
                                @click="onUpdateProductClick(item)"
                            >
                            </v-btn>
                            <v-btn
                                :disabled="isLoading"
                                variant="text"
                                icon="mdi-cash-multiple"
                                @click="onGetLatestProductCostClick(item)"
                            >
                            </v-btn>
                            <v-btn
                                :disabled="isLoading"
                                variant="text"
                                icon="mdi-ferry"
                                @click="onGetLatestEstimatedShippingFeeClick(item)"
                            >
                            </v-btn>
                            <v-btn
                                :disabled="isLoading"
                                :loading="isLoading"
                                variant="text"
                                icon="mdi-delete"
                                color="error"
                                @click="onDeleteProductClick(item)"
                            >
                            </v-btn>
                        </template>
                    </GenericListTable>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted, watch, ref } from 'vue';
import { useRoute } from 'vue-router';
import * as _ from 'lodash';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { ProductLabelTextHelper } from '../../utils/labelTextHelper/ProductLabelTextHelper';
import { CommonLabelTextHelper } from '../../utils/labelTextHelper/CommonLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { PathDefinition } from '../../utils/PathDefinition';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import GenericListTable from '../../components/GenericListTable.vue';
import DateRangeSelect from '../../components/DateRangeSelect.vue';
import { ProductHeaderHelper } from '../../utils/headerHelper/ProductHeaderHelper';
import { CsvFormatHelper } from '../../utils/CsvFormatHelper';
import { usePagination } from '../../composables/usePagination';
import { useMasterData } from '../../composables/useMasterData';
import { useUrlFilters } from '../../composables/useUrlFilters';
import { ProductFilterDefinitions } from '../../utils/filterDefinitions/ProductFilterDefinitions';

export default defineComponent({
    name: 'ListProduct',

    components: {
        ExportToCsv,
        AlertMessage,
        GenericListTable,
        DateRangeSelect
    },

    setup() {
        const route = useRoute();

        // Load master data options for dropdowns
        const { categoryOptions, loadAllMasterData } = useMasterData();

        const data = reactive({
            panel: [0],
            quickSearchVal: '',
            errorAlertMessage: '',
            searchFiltersDefault: {},
            searchFilters: {
                productId: null,
                sku: null,
                categoryCode: null,
                title: null,
                description: null
            },
            searchFilterDateRangeGrpDefault: {},
            searchFilterDateRangeGrp: {
                dateCreateAt: {
                    from: null,
                    to: null
                }
            },
            csvExportConfig: {
                filename: 'exportProduct.csv',
                csvHeader: [...ProductHeaderHelper.getCsvHeader()]
            },
            deleteOperation: {
                isLoading: false,
                showConfirmDialog: false,
                selectedProduct: null
            },
            validation: {
                hasErrors: false
            }
        });

        // Initialize URL filter sync
        const { syncFiltersToUrl, restoreFiltersFromUrl, watchRouteChanges } = useUrlFilters({
            filterFields: ProductFilterDefinitions,
            onFiltersRestored: (filters) => {
                // Apply restored filters to reactive state
                Object.keys(filters).forEach((key) => {
                    if (key.startsWith('date') && (key.endsWith('From') || key.endsWith('To'))) {
                        const dateField = key.replace(/From|To$/, '');
                        const direction = key.endsWith('From') ? 'from' : 'to';
                        data.searchFilterDateRangeGrp[dateField][direction] = filters[key];
                    } else {
                        data.searchFilters[key] = filters[key];
                    }
                });
            },
            validationContext: {
                categoryOptions
            }
        });

        // Initialize pagination
        const {
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            updateItemInAllPages,
            removeItemFromAllPages,
            initialize
        } = usePagination({
            apiEndpoint: '/api/staff/product/list',
            buildApiParams: (page: number) => {
                const params: any = {};

                // Text filters
                if (data.searchFilters.productId?.trim()) {
                    params.productId = data.searchFilters.productId.trim();
                }
                if (data.searchFilters.sku?.trim()) {
                    params.sku = data.searchFilters.sku.trim();
                }
                if (data.searchFilters.categoryCode?.trim()) {
                    params.categoryCode = data.searchFilters.categoryCode.trim();
                }
                if (data.searchFilters.title?.trim()) {
                    params.title = data.searchFilters.title.trim();
                }
                if (data.searchFilters.description?.trim()) {
                    params.description = data.searchFilters.description.trim();
                }

                // Add date range filtering if specified
                if (data.searchFilterDateRangeGrp.dateCreateAt.from) {
                    params.dateCreateAtFrom = Formatter.convertDateFromInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateCreateAt.from
                    );
                }

                if (data.searchFilterDateRangeGrp.dateCreateAt.to) {
                    params.dateCreateAtTo = Formatter.convertDateToInputToUtcDateStr(
                        data.searchFilterDateRangeGrp.dateCreateAt.to
                    );
                }

                return params;
            }
        });

        onMounted(async () => {
            // Load master data FIRST (for dropdown validation)
            await loadAllMasterData();

            // Clone defaults BEFORE restoring from URL
            data.searchFiltersDefault = _.cloneDeep(data.searchFilters);
            data.searchFilterDateRangeGrpDefault = _.cloneDeep(data.searchFilterDateRangeGrp);

            // Restore filters from URL (if any)
            const hasUrlFilters = Object.keys(route.query).length > 0;
            if (hasUrlFilters) {
                restoreFiltersFromUrl();
            }

            // Initialize pagination (single API call with restored filters)
            await initialize();

            // Watch for future route changes (back/forward buttons)
            watchRouteChanges();
        });

        // Watch for API errors from pagination composable
        const errorAlert = ref();
        watch(
            () => errorMessage.value,
            (newErrorMessage) => {
                if (newErrorMessage) {
                    data.errorAlertMessage = newErrorMessage;
                    errorAlert.value?.showAlert();
                }
            }
        );

        // Handle date range validation from component
        const onDateRangeValidationChange = (validationStatus: { hasErrors: boolean }) => {
            data.validation.hasErrors = validationStatus.hasErrors;
        };

        return {
            formLabelText: ProductLabelTextHelper.getLabelText,
            CommonLabelTextHelper,
            actionHeaderItem: ProductHeaderHelper.getActionHeaderItem(),
            headers: [...ProductHeaderHelper.getHeaders()],
            courierCodeList: [],
            data,
            // Master data options for dropdowns
            categoryOptions,
            // Pagination
            pagination,
            currentPageData,
            errorMessage,
            loadPage,
            resetPagination,
            nextPage,
            previousPage,
            selectPage,
            changePageSize,
            updateItemInAllPages,
            removeItemFromAllPages,
            errorAlert,
            onDateRangeValidationChange,
            // URL Filters
            syncFiltersToUrl
        };
    },
    computed: {},
    methods: {
        onKeydownQuickSearch(evt: any) {
            if (evt.key === 'Enter') {
                // Quick search is now handled by GenericListTable component
            }
        },
        async onResetClick() {
            this.data.searchFilters = _.cloneDeep(this.data.searchFiltersDefault);
            this.data.searchFilterDateRangeGrp = _.cloneDeep(this.data.searchFilterDateRangeGrpDefault);

            // Clear URL query params
            await this.syncFiltersToUrl({});

            // Reload with defaults
            this.resetPagination();
            await this.loadPage(1, true);
        },
        async onSearchClick() {
            if (this.data.validation.hasErrors) {
                this.data.errorAlertMessage = 'Please fix the date range errors before searching';
                this.errorAlert?.showAlert();
                return;
            }

            // Flatten filters for URL (handle nested date ranges)
            const currentFilters = {
                ...this.data.searchFilters,
                dateCreateAtFrom: this.data.searchFilterDateRangeGrp.dateCreateAt.from,
                dateCreateAtTo: this.data.searchFilterDateRangeGrp.dateCreateAt.to
            };

            // Sync to URL BEFORE API call
            await this.syncFiltersToUrl(currentFilters);

            // Reset pagination and search with new filters
            this.resetPagination();
            await this.loadPage(1, true);
        },
        async onImportClick() {
            await this.$router.push({ path: PathDefinition.importProduct });
        },
        async onImportProductCostClick() {
            await this.$router.push({ path: PathDefinition.importProductCost });
        },
        async onImportEstimatedShippingFeeClick() {
            await this.$router.push({ path: PathDefinition.importEstimatedShippingFee });
        },
        async onUpdateProductClick(item: any) {
            await this.$router.push({ path: `/updateProduct/${item.productId}` });
        },

        // Utility method for handling errors consistently
        handleError(error: any, message: string, showAlert: boolean = true) {
            console.error(message, error);
            if (showAlert) {
                this.data.errorAlertMessage = typeof error === 'string' ? error : message;
                this.$refs.errorAlert.showAlert();
            }
        },

        // Utility method for handling API responses
        handleApiResponse(response: any, successCallback: () => void, errorMessage?: string) {
            if (response.success) {
                successCallback();
            } else {
                const message = errorMessage || response.errors || 'Operation failed';
                this.handleError(response.errors, message);
            }
        },

        // Generic method to fetch and update product properties
        async fetchAndUpdateProductProperty(
            item: any,
            config: {
                apiEndpoint: string;
                responseDataPath: string;
                propertyName: string;
                defaultValue?: any;
            }
        ) {
            const { apiEndpoint, responseDataPath, propertyName, defaultValue = 0 } = config;
            const itemProductId = item.productId;
            const requestUrl = `/api/staff/${apiEndpoint}?productId=${itemProductId}`;

            try {
                const response = await fetchWrapper.get(requestUrl, '');

                this.handleApiResponse(
                    response,
                    () => {
                        const value = _.get(response, responseDataPath, defaultValue);
                        this.updateItemInAllPages(itemProductId, 'productId', propertyName, value);
                    },
                    `Failed to fetch ${propertyName}`
                );

                // If response fails, still update with default value
                if (!response.success) {
                    this.updateItemInAllPages(itemProductId, 'productId', propertyName, defaultValue);
                }
            } catch (error) {
                this.handleError(error, `Error fetching ${propertyName}`, false);
                this.updateItemInAllPages(itemProductId, 'productId', propertyName, defaultValue);
            }
        },

        // Refactored methods using the generic approach
        async onGetLatestProductCostClick(item: any) {
            await this.fetchAndUpdateProductProperty(item, {
                apiEndpoint: 'productCost/getEffectiveCost',
                responseDataPath: 'result.productCostAmount',
                propertyName: 'latestProductCost',
                defaultValue: 0
            });
        },

        async onGetLatestEstimatedShippingFeeClick(item: any) {
            await this.fetchAndUpdateProductProperty(item, {
                apiEndpoint: 'estimatedShippingFee/getEffectiveEstimatedShippingFee',
                responseDataPath: 'result.estimatedShippingFeeAmount',
                propertyName: 'latestEstimatedShippingFee',
                defaultValue: 0
            });
        },
        csvFormatter: function (value: unknown, key: string, row: Record<string, unknown>): unknown {
            return CsvFormatHelper.formatDateTimeCsvValue(value as string, key);
        },
        onDeleteProductClick(item: any) {
            this.data.deleteOperation.selectedProduct = item;
            this.data.deleteOperation.showConfirmDialog = true;
        },
        onCancelDelete() {
            this.data.deleteOperation.showConfirmDialog = false;
            this.data.deleteOperation.selectedProduct = null;
        },
        async onConfirmDelete() {
            if (!this.data.deleteOperation.selectedProduct) {
                return;
            }

            this.data.deleteOperation.isLoading = true;

            try {
                const productId = this.data.deleteOperation.selectedProduct.productId;
                const requestUrl = `/api/staff/product/batchDelete`;
                const requestData = {
                    productIdList: [productId]
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                this.handleApiResponse(
                    response,
                    () => {
                        // Remove the item using the pagination composable
                        this.removeItemFromAllPages(productId, 'productId');

                        // Close dialog and reset state
                        this.data.deleteOperation.showConfirmDialog = false;
                        this.data.deleteOperation.selectedProduct = null;
                    },
                    'Failed to delete product'
                );
            } catch (error) {
                this.handleError(error, 'An unexpected error occurred while deleting the product');
            } finally {
                this.data.deleteOperation.isLoading = false;
            }
        },
        // Pagination event handlers
        async onNextPage() {
            await this.nextPage();
        },

        onPreviousPage() {
            this.previousPage();
        },

        onPageSelect(selectedPage: number) {
            this.selectPage(selectedPage);
        },

        async onPageSizeChange() {
            await this.changePageSize();
        }
    }
});
</script>

<style></style>
